// src/components/PerformanceReportPDF.js
import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Font } from '@react-pdf/renderer';
import { format } from 'date-fns';

// Register fonts (you might need to install these or use default fonts)
Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf', fontWeight: 300 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 400 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf', fontWeight: 500 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 700 },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 20,
    borderBottom: '2 solid #3078AC',
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#3078AC',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 12,
    color: '#666666',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 10,
    color: '#333333',
  },
  card: {
    border: '1 solid #E0E0E0',
    borderRadius: 4,
    padding: 15,
    marginBottom: 10,
    backgroundColor: '#F9F9F9',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 8,
    color: '#333333',
  },
  metricsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  metricLabel: {
    fontSize: 10,
    color: '#666666',
  },
  metricValue: {
    fontSize: 10,
    fontWeight: 500,
  },
  progressBar: {
    height: 6,
    backgroundColor: '#E0E0E0',
    borderRadius: 3,
    marginTop: 5,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  executiveSummary: {
    marginTop: 20,
  },
  summaryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    padding: 8,
    backgroundColor: '#F5F5F5',
    borderRadius: 4,
  },
  summaryTitle: {
    fontSize: 12,
    fontWeight: 500,
    flex: 2,
  },
  summaryValue: {
    fontSize: 11,
    flex: 1,
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: '#666666',
  },
});

const PerformanceReportPDF = ({ data, filters, reportDate }) => {
  const getProgressColor = (achieved, target) => {
    const percentage = (achieved / target) * 100;
    if (percentage > 70) return '#4CAF50';
    if (percentage > 40) return '#2196F3';
    return '#F44336';
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num.toFixed(0)).toLocaleString();
  };

  const mainMetrics = data.filter(item => 
    ['Loan Amount', 'Collection Rate', 'Portfolio at Risk'].includes(item.title)
  );

  const executiveMetrics = data.filter(item => 
    !['Loan Amount', 'Collection Rate', 'Portfolio at Risk'].includes(item.title)
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Performance Dashboard Report</Text>
          <Text style={styles.subtitle}>
            Generated on: {format(new Date(reportDate), 'MMMM dd, yyyy HH:mm')}
          </Text>
          {filters.region && (
            <Text style={styles.subtitle}>Region: {filters.region}</Text>
          )}
          {filters.branch && (
            <Text style={styles.subtitle}>Branch: {filters.branch}</Text>
          )}
          {filters.fieldOfficer && (
            <Text style={styles.subtitle}>Field Officer: {filters.fieldOfficer}</Text>
          )}
        </View>

        {/* Main Metrics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Performance Indicators</Text>
          {mainMetrics.map((item, index) => (
            <View key={index} style={styles.card}>
              <Text style={styles.cardTitle}>
                {item.title === 'Loan Amount' ? 'Disbursed Amount' : 
                 item.title === 'Collection Rate' ? 'Collection Rate' : 
                 'Portfolio at Risk'}
              </Text>
              
              <View style={styles.metricsRow}>
                <Text style={styles.metricLabel}>Achieved:</Text>
                <Text style={styles.metricValue}>
                  {item.title === 'Collection Rate' || item.title === 'Portfolio at Risk' 
                    ? `${item.achieved}%` 
                    : `$${formatNumber(item.achieved)}`}
                </Text>
              </View>
              
              <View style={styles.metricsRow}>
                <Text style={styles.metricLabel}>Target:</Text>
                <Text style={styles.metricValue}>
                  {item.title === 'Collection Rate' || item.title === 'Portfolio at Risk' 
                    ? `${item.target}%` 
                    : `$${formatNumber(item.target)}`}
                </Text>
              </View>
              
              <View style={styles.metricsRow}>
                <Text style={styles.metricLabel}>Remaining:</Text>
                <Text style={styles.metricValue}>
                  {item.title === 'Collection Rate' || item.title === 'Portfolio at Risk' 
                    ? `${item.remaining}%` 
                    : `$${formatNumber(item.remaining)}`}
                </Text>
              </View>

              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { 
                      width: `${Math.min(100, (item.achieved / item.target) * 100)}%`,
                      backgroundColor: getProgressColor(item.achieved, item.target)
                    }
                  ]} 
                />
              </View>
            </View>
          ))}
        </View>

        {/* Executive Summary */}
        <View style={styles.executiveSummary}>
          <Text style={styles.sectionTitle}>Executive Summary</Text>
          {executiveMetrics.map((item, index) => (
            <View key={index} style={styles.summaryItem}>
              <Text style={styles.summaryTitle}>{item.title}</Text>
              <Text style={styles.summaryValue}>
                Achieved: {formatNumber(item.achieved)} | 
                Target: {formatNumber(item.target)} | 
                Remaining: {formatNumber(item.remaining)}
              </Text>
            </View>
          ))}
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          Confidential Performance Report - Generated by Loan Management System
        </Text>
      </Page>
    </Document>
  );
};

export default PerformanceReportPDF;